import { Injectable } from '@angular/core';
import { API } from './api';
import { BaseProvider } from './base';
import { BehaviorSubject } from 'rxjs';

/**
 * This provider allows the app to
 * cache and query information regarding a user
 */

export class User {
    email: string;
    name: string;
    userIdentifier: string;
    account_tier: string;
    capabilities: object = {};
    permissions: object = {};
    organization: UserOrganization = new UserOrganization();
    usageStatsLimits: UsageStatsLimits;
    usageStatsTotals: UsageStatsTotals;
}

export enum UserRole {
    MANAGER = 'manager',
    USER = 'user',
    TAGGER = 'tagger'
}

export interface UsageStatsLimits {
    'Max API Inferences Per Month': number;
    'Max Active Users': number;
    'Max Published Brains': number;
    'Max Storage In GB': number;
}

export interface UsageStatsTotals {
    apiCalls: number;
    diskSpace: number;
    publishedBrains: number;
    usersInOrganization: number;
}

export class UserOrganization {
    name: string;
    organizationIdentifier: string;
    role: string;
}

enum Permissions {
    MANAGE_ORGANIZATION = 'Manage Organization',
    MANAGE_USERS = 'Manage Users',
    MANAGE_PROJECTS = 'Manage Projects',
    MANAGE_DATASETS = 'Manage Datasets',
    MANAGE_CLASSES = 'Manage Classes',
    MANAGE_ASSETS = 'Manage Assets',
    MANAGE_EXPORTS = 'Manage Exports',
    MANAGE_USER_ASSIGNMENTS = 'Manage User Assignments',
    MANAGE_TAGS = 'Manage Tags',
    MANAGE_API_KEYS = 'Manage API Keys'
}

export enum Capabilities {
    Organization = 'Enable Organization',
    SwitchAccount = 'Enable Switch Account',
    APIUsage = 'Max API Inferences Per Month',
    EnableDetection = 'Enable Detection',
    EnableDetectionBrains = 'Enable Detection Brains',
    EnableDetectionTrainingTesting = 'Enable Detection Training Testing',
    EnableAnomalyRecognition = 'Enable Anomaly Recognition',
    EnableSceneRecognition = 'Enable Scene Recognition',
    EnableSegmentation = 'Enable Segmentation',
    EnableROI = 'Enable ROI',
    EnableBrainExport = 'Enable Brain Export',
    EnableWhitelabelDownload = 'Enable Whitelabel Download',
    EnableDetectionWorkspaceBrain = 'Enable Detection Workspace Brain',
    EnableDatasetDuplication = 'Enable Dataset Duplication',
    EnableInferenceAPI = 'Enable Inference API',
    EnableBrainPromotion = 'Enable Brain Promotion',
    MaxDatasets = 'Max Datasets',
    MaxProjects = 'Max Projects',
    EnableBrainOptimization = 'Enable Brain Optimization',
    EnableBrainFreezing = 'Enable Brain Freezing',
    EnableSDKAPI = 'Enable SDK API',
    EnableAnomalyHiFi = 'Enable Anomaly Hi-Fi',
    EnableExperimentalFeatures = 'Enable Experimental Features',
    EnableLicensedFeatureControl = 'Enable Licensed Feature Control',
    BackboneTargetTierNumber = 'Backbone Target Tier Number',
    EnableAnomalyThresholdTuning = 'Enable Anomaly Threshold Tuning',
    EnablePermanentFileDeletion = 'Enable Permanent File Deletion'
}

@Injectable()
export class UserProvider extends BaseProvider {
    private user: User = new User();
    private allowedEmails: string[] = [
        'liam@neurala.com',
        'aplumb@neurala.com',
        'vishal@neurala.com',
        'rakesh@neurala.com',
        'emitchell@neurala.com',
        'pmaheshwari@neurala.com',
        'bgong@neurala.com',
        'demo@neurala.com'
    ];

    public Capabilities = Capabilities;

    public userLoaded: BehaviorSubject<User> = new BehaviorSubject<User>(this.user);

    constructor(public api: API) {
        super();
    }

    public async fetchUserInformation(): Promise<User> {
        try {
            const userInformation = await this.api.get('users/information');
            const result = userInformation.message;
            const capabilities = {};

            if (result.capabilities) {
                result.capabilities.forEach(capability => {
                    capabilities[capability.name] = {
                        value: capability.value,
                        enabled: capability.enabled
                    };
                });
            }
            this.user = new User();
            this.user.email = result.email;
            this.user.name = result.name;
            this.user.userIdentifier = result.userIdentifier;
            this.user.account_tier = result.account_tier;
            this.user.capabilities = capabilities;
            this.user.permissions = {};
            this.user.organization = result.organization || new UserOrganization();
            this.user.usageStatsLimits = result.usageStatsLimits;
            this.user.usageStatsTotals = result.usageStatsTotals;

            if (!this.isEmpty(result.permissions)) {
                for (const key of result.permissions) {
                    this.user.permissions[key] = true;
                }
            }

            this.userLoaded.next(this.user);

            return this.user;
        } catch (e) {
            throw e;
        }
    }

    public async fetchUserInformationDebounce(): Promise<User> {
        return this.debounce(
            'userInformation',
            async () => {
                return await this.fetchUserInformation();
            },
            250
        );
    }

    public async load(force: boolean = false) {
        return await this.ensureLoaded(() => this.fetchUserInformationDebounce(), force);
    }

    public getUserInformation(): User {
        return this.user;
    }

    /**
     * Return information about the user's broad role in an organization (or lack thereof).
     * This is currently used in the dashboard and dataset page components.
     */
    public getUserRole(): UserRole {
        const canManageDatasets = this.canManageDatasets();
        const hasOrganization = this.getUserInformation().organization.organizationIdentifier;
        if (!canManageDatasets) {
            return UserRole.TAGGER;
        } else {
            return hasOrganization ? UserRole.MANAGER : UserRole.USER;
        }
    }

    // check if object is empty or not
    public isEmpty(obj: any): boolean {
        for (const key in obj) {
            if (obj.hasOwnProperty(key)) {
                return false;
            }
        }
        return true;
    }

    // if no permission is defined user is not part of organization
    public hasPermission(key: string): boolean {
        return this.isEmpty(this.user.permissions) ? true : this.user.permissions[key] == true;
    }

    public canManageOrganization(): boolean {
        return this.hasPermission(Permissions.MANAGE_ORGANIZATION);
    }

    public canManageAPIKeys(): boolean {
        return this.hasPermission(Permissions.MANAGE_API_KEYS);
    }

    public canManageProjects(): boolean {
        return this.hasPermission(Permissions.MANAGE_PROJECTS);
    }

    public canManageDatasets(): boolean {
        return this.hasPermission(Permissions.MANAGE_DATASETS);
    }

    public canManageAssets(): boolean {
        return this.hasPermission(Permissions.MANAGE_ASSETS);
    }

    public canManageClasses(): boolean {
        return this.hasPermission(Permissions.MANAGE_CLASSES);
    }

    public canManageExports(): boolean {
        return this.hasPermission(Permissions.MANAGE_EXPORTS);
    }

    public canManageUserAssignments(): boolean {
        return this.hasPermission(Permissions.MANAGE_USER_ASSIGNMENTS);
    }

    public canManageUser(): boolean {
        return this.hasPermission(Permissions.MANAGE_USERS);
    }

    public canManageTags(): boolean {
        return this.hasPermission(Permissions.MANAGE_TAGS);
    }

    public canBuildBrains(): boolean {
        return this.isNeurala();
    }

    public canExportBrains(): boolean {
        return this.checkCapability(Capabilities.EnableBrainExport);
    }

    public canSwitchAccount(): boolean {
        return this.checkCapability(Capabilities.SwitchAccount);
    }

    public hasOrganizationCapability(): boolean {
        return this.checkCapability(Capabilities.Organization);
    }

    public hasAPIUsageCapability(): boolean {
        return this.checkCapability(Capabilities.APIUsage);
    }

    public hasEnableBrainOptimizationCapability(): boolean {
        return this.checkCapability(Capabilities.EnableBrainOptimization);
    }

    public hasEnableBrainFreezingCapability(): boolean {
        return this.checkCapability(Capabilities.EnableBrainFreezing);
    }

    public hasDetectionCapability(): boolean {
        return this.checkCapability(Capabilities.EnableDetection);
    }

    public hasEnableAnomalyRecognitionCapability(): boolean {
        return this.checkCapability(Capabilities.EnableAnomalyRecognition);
    }

    public hasSceneRecognitionCapability(): boolean {
        return this.checkCapability(Capabilities.EnableSceneRecognition);
    }

    public hasSegmentationCapability(): boolean {
        return this.checkCapability(Capabilities.EnableSegmentation);
    }

    public hasDetectionBrainsCapability(): boolean {
        return this.checkCapability(Capabilities.EnableDetectionBrains);
    }

    public hasDetectionWorkspaceBrainCapability(): boolean {
        return this.checkCapability(Capabilities.EnableDetectionWorkspaceBrain);
    }

    public isDetectionTrainingTester(): boolean {
        return this.checkCapability(Capabilities.EnableDetectionTrainingTesting);
    }

    public hasRegionOfInterestCapability(): boolean {
        return this.checkCapability(Capabilities.EnableROI);
    }

    public hasDatasetDuplicationCapability(): boolean {
        return this.checkCapability(Capabilities.EnableDatasetDuplication);
    }

    public hasInferenceAPICapability(): boolean {
        return this.checkCapability(Capabilities.EnableInferenceAPI);
    }

    public hasWhitelabelDownloadCapability(): boolean {
        return this.checkCapability(Capabilities.EnableWhitelabelDownload);
    }

    public hasBrainPromotionCapability(): boolean {
        return this.checkCapability(Capabilities.EnableBrainPromotion);
    }

    public hasSDKAPICapability(): boolean {
        return this.checkCapability(Capabilities.EnableSDKAPI);
    }

    public hasAnomalyHiFiCapability(): boolean {
        return this.checkCapability(Capabilities.EnableAnomalyHiFi);
    }

    public hasExperimentalFeaturesCapability(): boolean {
        return this.checkCapability(Capabilities.EnableExperimentalFeatures);
    }

    public canViewAPIKeysPage(): boolean {
        return this.canManageAPIKeys() && (this.hasInferenceAPICapability() || this.hasSDKAPICapability());
    }

    public isNeurala(): boolean {
        return this.allowedEmails.includes(this.user.email);
    }

    public hasEnableAnomalyHiFiCapability(): boolean {
        return this.checkCapability(Capabilities.EnableAnomalyHiFi);
    }

    public hasEnableLicensedFeatureControl(): boolean {
        return this.checkCapability(Capabilities.EnableLicensedFeatureControl);
    }

    public hasEnableAnomalyThresholdTuningCapability(): boolean {
        return this.checkCapability(Capabilities.EnableAnomalyThresholdTuning);
    }

    public hasEnablePermanentFileDeletionCapability(): boolean {
        return this.checkCapability(Capabilities.EnablePermanentFileDeletion);
    }

    public async reset(): Promise<any> {
        try {
            return await this.api.patch('user/reset');
        } catch (e) {
            throw e;
        }
    }

    /**
     * check whether a user has a capability or not
     * @param capability capability name
     */
    public checkCapability(capability: string): boolean {
        if (!this.user.capabilities[capability]) {
            return false;
        }
        return this.user.capabilities[capability].enabled == 1;
    }

    public resetInfo(): void {
        super.resetInfo();
    }

    public getCapabilityLimit(capability: string): number | null {
        if (this.user.capabilities[capability]) {
            return this.user.capabilities[capability].value;
        } else {
            return null;
        }
    }

    public isSonyUser(): boolean {
        return this.user.account_tier.includes('Sony');
    }

    public isQCUser(): boolean {
        return this.user.account_tier.includes('Qualcomm');
    }

    public isTaggerUser(): boolean {
        return this.getUserRole() === UserRole.TAGGER;
    }

    public getBackboneTargetTierNumber(): number | null {
        return this.getCapabilityLimit(Capabilities.BackboneTargetTierNumber);
    }

    public hasDefaultBackboneTargetTier(): boolean {
        const number = this.getBackboneTargetTierNumber();
        return number == null || number == 1;
    }

    public getUpgradeLink(): string | undefined {
        const user = this.getUserInformation();

        // this prevents user from being undefined and breaking the page if there's a delay in fetching the user in ngOnInit.
        if (user.name) {
            const nameSplit = user.name.split(' ');
            // This assumes that the user has only a first and last name; this might be difficult for non-Western names or those with double names
            const firstName = nameSplit.shift();
            const lastName = nameSplit.join(' ');
            const email = user.email.replace(/\+/g, '%2B');

            return `https://info.neurala.com/upgrade?firstname=${firstName}&lastname=${lastName}&email=${email}`;
        }
    }

    /**
     * If this user's account tier has Licensed Feature Control, return the feature availablity we're given
     * Otherwise it should always be available
     */
    public hasLicensedFeatureIfRequired(feature: boolean): boolean {
        return this.hasEnableLicensedFeatureControl() ? feature : true;
    }
}



// WEBPACK FOOTER //
// ./src/providers/user.ts